import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FormTextArea } from '../../components';
import ConfirmDialog from '../../components/ConfirmDialog';
import { AdminPageLayout, Section, Card, Badge } from '../../components';
import {
  Listing,
  ListingActionBar,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';
import {
  EditAction,
  DeleteAction,
  ViewAction,
  CreateAction
} from '../../components/listing/v2/ListingAction';
import { Users, ShieldCheck, Globe, Activity, Eye, Pencil, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/api';

const RoleTemplateManagement = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    staff_only: 'all',
    search: ''
  });
  const [showDeactivateModal, setShowDeactivateModal] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const activeOnly = filters.status === 'active';
      const templatesData = await apiService.getRoleTemplates(activeOnly);
      setTemplates(templatesData.templates || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters.status]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleDeactivateTemplate = async (templateId) => {
    if (!deactivateReason.trim()) {
      showError('Please provide a reason for deactivation');
      return;
    }

    try {
      await apiService.deactivateRoleTemplate(templateId);
      showSuccess('Role template deactivated successfully');
      setShowDeactivateModal(null);
      setDeactivateReason('');
      loadTemplates();
    } catch (err) {
      showError(`Error deactivating role template: ${err.message}`);
    }
  };

  const handleActivateTemplate = async (templateId) => {
    try {
      await apiService.activateRoleTemplate(templateId);
      showSuccess('Role template activated successfully');
      loadTemplates();
    } catch (err) {
      showError(`Error activating role template: ${err.message}`);
    }
  };

  const handleDeleteTemplate = async (templateId, templateName) => {
    setIsDeleting(true);

    try {
      await apiService.deleteRoleTemplate(templateId);
      showSuccess(`Role template "${templateName}" deleted permanently.`);
      setShowDeleteModal(null);
      setTemplates(prevTemplates => prevTemplates.filter(t => t.id !== templateId));
      await loadTemplates();
    } catch (err) {
      showError(`Error deleting role template: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredTemplates = templates.filter(template => {
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      if (!template.name.toLowerCase().includes(searchTerm) &&
        !template.description.toLowerCase().includes(searchTerm)) {
        return false;
      }
    }
    if (filters.staff_only !== 'all') {
      const isStaffFilter = filters.staff_only === 'staff';
      if (template.is_staff !== isStaffFilter) return false;
    }
    return true;
  });

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      filters={[
        {
          name: 'status',
          label: 'Status',
          options: [
            { value: 'all', label: 'All Statuses' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' }
          ]
        },
        {
          name: 'staff_only',
          label: 'Role Type',
          options: [
            { value: 'all', label: 'All Types' },
            { value: 'staff', label: 'Staff Roles' },
            { value: 'user', label: 'User Roles' }
          ]
        }
      ]}
      activeFilters={{ status: filters.status, staff_only: filters.staff_only }}
      onFilterChange={(name, val) => handleFilterChange(name, val)}
      onClearFilters={() => setFilters({ status: 'all', staff_only: 'all', search: '' })}
    />
  );

  const headerActions = (
    <ListingActionBar
      rightSection={
        <CreateAction onClick={() => navigate('/admin/role-templates/create')} label="DESIGN NEW PATTERN" />
      }
    />
  );

  return (
    <AdminPageLayout
      title="ROLE ARCHITECTURE"
      subtitle="Standardized permission sets for system participants"
      backPath="/admin/system-templates"
      maxWidth="1400px"
    >
      <Section title="FRAMEWORK DEFINITIONS">
        <Listing
          loading={loading}
          items={filteredTemplates}
          actions={headerActions}
          filters={filterBar}
          variant="list"
          renderItem={(template) => (
            <ListingItem
              key={template.id}
              title={template.name}
              subtitle={template.description}
              icon={template.is_staff ? <ShieldCheck size={32} /> : <Users size={32} />}
              actions={
                <div style={{ display: 'flex', gap: '8px' }}>
                  <ViewAction size="xs" onClick={() => navigate(`/admin/role-templates/${template.id}`)} title="Inspect Pattern" />
                  <EditAction size="xs" onClick={() => navigate(`/admin/role-templates/${template.id}/edit`)} title="Modify Pattern" />
                  {template.is_active ? (
                    <DeleteAction label="Suspend" size="xs" onClick={() => setShowDeactivateModal(template)} title="Suspend Pattern" />
                  ) : (
                    <EditAction label="Restore" variant="primary" size="xs" onClick={() => handleActivateTemplate(template.id)} icon={<CheckCircle size={14} />} title="Restore Pattern" />
                  )}
                  <DeleteAction size="xs" onClick={() => setShowDeleteModal(template)} title="Purge Pattern" />
                </div>
              }
            >
              <div style={{ display: 'flex', gap: '2.5rem', marginTop: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', opacity: 0.5, letterSpacing: '0.05em' }}>Deployment Scope</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem', fontWeight: 600 }}>
                    <Globe size={14} style={{ opacity: 0.5 }} /> {template.usage_count} Active Tenants
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', opacity: 0.5, letterSpacing: '0.05em' }}>Logic Rules</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem', fontWeight: 600 }}>
                    <Activity size={14} style={{ opacity: 0.5 }} /> {template.permissions.length} Defined Protocols
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', marginLeft: 'auto', alignSelf: 'flex-end' }}>
                  <Badge variant={template.is_staff ? "primary" : "outline"} size="sm">
                    {template.is_staff ? "STAFF AUTHORITY" : "STANDARD IDENTITY"}
                  </Badge>
                  {template.is_active ? (
                    <Badge variant="success" size="sm">FUNCTIONAL</Badge>
                  ) : (
                    <Badge variant="warning" size="sm">SUSPENDED</Badge>
                  )}
                </div>
              </div>
            </ListingItem>
          )}
          emptyMessage="No pattern definitions identified"
        />
      </Section>

      {/* Deactivate Modal */}
      {showDeactivateModal && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setShowDeactivateModal(null)}
          onConfirm={() => handleDeactivateTemplate(showDeactivateModal.id)}
          title="Suspend Governance Pattern"
          variant="warning"
          confirmText="Confirm Suspension"
          disabled={!deactivateReason.trim()}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <p>
              Are you sure you want to suspend the <strong>{showDeactivateModal.name}</strong> governance pattern?
            </p>
            <FormTextArea
              label="Strategic Rationale"
              name="deactivate-reason"
              value={deactivateReason}
              onChange={(e) => setDeactivateReason(e.target.value)}
              placeholder="Document the operational rationale for this suspension..."
              rows={3}
              required
            />
            <p style={{ opacity: 0.7, fontSize: '0.85rem' }}>
              Suspension prevents new assignments to this pattern. Existing occupants will maintain their current privileges until manually adjusted.
            </p>
          </div>
        </ConfirmDialog>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setShowDeleteModal(null)}
          onConfirm={() => handleDeleteTemplate(showDeleteModal.id, showDeleteModal.name)}
          title="Permanent Logical Purge"
          variant="danger"
          confirmText="Finalize Purge"
          requireTextConfirmation
          confirmationText={showDeleteModal.name}
          loading={isDeleting}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <p>
              Initiating permanent decommission of <strong>{showDeleteModal.name}</strong>.
            </p>
            <div style={{ backgroundColor: 'var(--color-error-light)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--color-error)' }}>
              <p style={{ color: 'var(--color-error)', fontSize: '0.85rem', fontWeight: 600 }}>
                This will permanently erase the logical template from the platform registry. Entities currently utilizing this pattern will persist, but the underlying definition will be inaccessible.
              </p>
            </div>
            <p style={{ fontSize: '0.9rem', opacity: 0.8 }}>
              Type the pattern name to authorize final deletion.
            </p>
          </div>
        </ConfirmDialog>
      )}
    </AdminPageLayout>
  );
};

export default RoleTemplateManagement;